<template>
    <div class="KbSectionTree">
        <div class="KbSectionTree__header">
            <EidIcon class="KbSectionTree__icon" :icon-src="faFolderOpen" />
            <h3 class="KbSectionTree__name">
                <KbLink
                    class="KbSectionTree__nameLink"
                    :target="{ kind: 'section', id: section.id }"
                >
                    {{ section.name }}
                </KbLink>
            </h3>
        </div>
        <p v-if="section.description" class="KbSectionTree__description">
            {{ section.description }}
        </p>
        <ul class="KbSectionTree__articles">
            <li v-for="article in orderedArticles" :key="article.id" class="KbSectionTree__article">
                <KbLink
                    class="KbSectionTree__articleLink"
                    :target="{ kind: 'article', id: article.id }"
                >
                    <EidIcon class="KbSectionTree__articleIcon" :icon-src="faFileLines" />
                    <span class="KbSectionTree__articleTitle">{{ article.title }}</span>
                    <EidIcon class="KbSectionTree__articleChevron" :icon-src="faChevronRight" />
                </KbLink>
            </li>
        </ul>
        <div v-if="orderedSections.length" class="KbSectionTree__children">
            <KbSectionTree
                v-for="child in orderedSections"
                :key="child.id"
                :section="child"
                class="KbSectionTree--nested"
            />
        </div>
    </div>
</template>

<style lang="scss">
.KbSectionTree {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px 24px;
    background: var(--eidWhite);
    border: 1px solid var(--eidTechGray-25);
    border-radius: 10px;

    &__header {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &__icon {
        flex-shrink: 0;
        font-size: 18px;
        color: var(--eidSecureBlue-75);
    }

    &__name {
        margin: 0;
        font-size: var(--text-section-header-size, 16px);
        font-weight: 700;
        line-height: 1.3;
    }

    &__nameLink {
        color: var(--eidSecureBlue);
        text-decoration: none;

        &:hover {
            color: var(--eidTrustGreen);
            text-decoration: underline;
        }

        &:focus-visible {
            outline: none;
            border-radius: 4px;
            box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidTrustGreen);
        }
    }

    &__description {
        margin: 0;
        font-size: var(--text-body-size, 14px);
        color: var(--eidSecureBlue-75);
        line-height: 1.5;
    }

    &__articles {
        display: flex;
        flex-direction: column;
        gap: 2px;
        margin: 0;
        padding: 0;
        list-style: none;
    }

    &__articleLink {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        margin: 0 -10px;
        border-radius: 6px;
        color: var(--eidSecureBlue-75);
        text-decoration: none;
        transition: background-color 0.15s ease, color 0.15s ease;

        &:hover {
            background-color: var(--eidTechGray-10);
            color: var(--eidSecureBlue);
        }

        &:hover .KbSectionTree__articleChevron {
            opacity: 1;
            transform: translateX(0);
        }

        &:focus-visible {
            outline: none;
            box-shadow: 0 0 0 2px var(--eidTrustGreen);
        }
    }

    &__articleIcon {
        flex-shrink: 0;
        font-size: 13px;
        color: var(--eidTechGray-75);
    }

    &__articleTitle {
        flex: 1;
        font-size: var(--text-body-size, 14px);
    }

    &__articleChevron {
        flex-shrink: 0;
        font-size: 11px;
        opacity: 0;
        transform: translateX(-4px);
        transition: opacity 0.15s ease, transform 0.15s ease;
    }

    &__children {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-top: 4px;
        padding-top: 12px;
        border-top: 1px solid var(--eidTechGray-10);
    }

    &--nested {
        padding: 0;
        background: transparent;
        border: none;
        border-radius: 0;

        .KbSectionTree__icon {
            font-size: 14px;
        }

        .KbSectionTree__name {
            font-size: var(--text-body-size, 14px);
            font-weight: 700;
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faChevronRight, faFileLines, faFolderOpen } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { KbArticleStub, KbSection } from '@evidentid/tprm-portal-lib/models/kb';

    import KbLink from '@/modules/kb/components/KbLink/KbLink.vue';

    export default defineComponent({
        name: 'KbSectionTree',
        components: { EidIcon, KbLink },
        props: {
            section: {
                type: Object as PropType<KbSection>,
                required: true,
            },
        },
        data() {
            return { faChevronRight, faFileLines, faFolderOpen };
        },
        computed: {
            orderedArticles(): KbArticleStub[] {
                return [ ...this.section.articles ].sort((a, b) => a.position - b.position);
            },
            orderedSections(): KbSection[] {
                return [ ...this.section.sections ].sort((a, b) => a.position - b.position);
            },
        },
    });
</script>
